// Persisted workspace layout: `{schemaVersion, layout}` per the roadmap's
// explicit call to version this from the first commit, since none of the
// docking libraries surveyed version their own saved-layout shape. Stored
// per project (a saved layout is meaningless across different projects/
// dimensions) under a single localStorage key.
//
// Loading is deliberately conservative: any shape mismatch, parse failure,
// or schema-version drift is treated as "no saved layout" rather than
// thrown - Workspace.tsx then falls back to building the default preset
// layout fresh, which is also exactly what "Reset layout" does. See
// normalizeLayout.ts for how the `layout` payload itself gets validated
// against the panels actually registered right now.
import type { SerializedDockview } from 'dockview-react';
import type { PanelId, PresetId } from './types';

// Bumped 1 -> 2 alongside the orphaned-tab-bug fix (Workspace.tsx's
// activation-effect fallback + the Panels menu): a persisted layout saved
// under the old bug's behaviour is safest treated as absent rather than
// restored, per this file's own documented migration path.
//
// Bumped 2 -> 3 when the Scene preset's panel set changed shape: Environment
// stopped being three panels pinned to the left/centre/right zones and
// became one zone-less panel composed in scenePanels.tsx. normalizeLayout
// prunes the three ids that no longer exist, but it cannot invent a node
// for the new `environment` id - so a layout saved under the old shape
// restores without it, and Workspace's own "which registered panels did
// fromJSON not bring back" diff then reports it as deliberately closed,
// leaving Scene permanently showing "Panels (1 hidden)" with no
// Environment tab. Discarding those layouts once is the fix, and is exactly
// the migration path this file documents for a shape change.
export const WORKSPACE_LAYOUT_SCHEMA_VERSION = 3;

export interface PersistedWorkspaceState {
  schemaVersion: typeof WORKSPACE_LAYOUT_SCHEMA_VERSION;
  activePresetId: PresetId;
  /** Last-active tab within each preset's tab strip, so switching back to a
   * preset resumes where the creator left it. */
  activeTabByPreset: Partial<Record<PresetId, PanelId>>;
  layout: SerializedDockview;
}

function storageKey(projectId: string): string {
  return `ss-editor-workspace-layout:${projectId}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Structural sanity check only - not a deep schema validation. Anything
 * that survives this still goes through normalizeLayout's panel-id pruning
 * before ever reaching dockview's `fromJSON`. */
function isPersistedWorkspaceState(value: unknown): value is PersistedWorkspaceState {
  if (!isPlainObject(value)) return false;
  if (value.schemaVersion !== WORKSPACE_LAYOUT_SCHEMA_VERSION) return false;
  if (typeof value.activePresetId !== 'string') return false;
  if (!isPlainObject(value.activeTabByPreset)) return false;
  if (!isPlainObject(value.layout)) return false;
  const layout = value.layout;
  if (!isPlainObject(layout.grid) || !isPlainObject(layout.grid.root) || !isPlainObject(layout.panels)) return false;
  return true;
}

export function loadPersistedWorkspaceState(projectId: string): PersistedWorkspaceState | null {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPersistedWorkspaceState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function savePersistedWorkspaceState(projectId: string, state: PersistedWorkspaceState): void {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(state));
  } catch {
    // Storage full/disabled/private-browsing: layout persistence is a
    // convenience, not correctness-critical - drop the write silently.
  }
}

export function clearPersistedWorkspaceState(projectId: string): void {
  try {
    localStorage.removeItem(storageKey(projectId));
  } catch {
    /* ignore */
  }
}
